import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import PlantMilkProduct from 'models/PlantMilkProduct'
import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import Switch from 'react-switch'
import { Col, FormGroup, Input, Row, Alert } from 'reactstrap'
import { useFormUtils } from 'views/shared/useFormUtils'
import {
  packagingInitialState,
  milkTypeOptions,
  flavorOptions,
  typeOptions,
} from './constants'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const PlantMilkPackage = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const { onChange, setSelect, setSwitch, asterisk } = useFormUtils()

  const methods = useFormContext()

  const optionsReset = selected => {
    if (selected?.value !== 'Other') {
      methods.setValue('OtherMilkType', '')
    }
  }

  useEffect(() => {
    setScrollToTop(true)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      ['52', '55'].includes(current?.category) &&
      ['', '011', '001', '002', '003', '004', '005', '006', '007', '008'].includes(
        current?.subCategory
      )
    ) {
      const {
        category,
        subCategory,
        MilkType,
        OtherMilkType,
        Flavor,
        Type,
        Size,
        Organic,
        MultiPackageType,
        MultiPackageCount,
      } = current
      setPackaging({
        category,
        subCategory,
        MilkType: {
          value: MilkType || '',
          label:
            MilkType?.length > 0
              ? MilkType
              : 'Plant-Based Milk Alternative Type',
        },
        OtherMilkType: OtherMilkType,
        Flavor: {
          value: Flavor || '',
          label: Flavor?.length > 0 ? Flavor : 'Flavor',
        },
        Type: {
          value: Type || '',
          label: Type?.length > 0 ? Type : 'Type',
        },
        Size: {
          value: Size || '',
          label: Size?.length > 0 ? Size : 'Size',
        },
        Organic: Organic ?? false,
        MultiPackageType: MultiPackageType ?? false,
        MultiPackageCount: MultiPackageCount ?? null,
      })
    } else if (!updating) {
      setCurrent(PlantMilkProduct)
      setPackaging(packagingInitialState)
    }
    console.log(current)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  useEffect(() => {
    if (updating) {
      if (current?.MultiPackageType) {
        methods.setValue('MultiPackageCount', current.MultiPackageCount)
      }
      if (current?.MilkType === 'Other') {
        methods.setValue('OtherMilkType', current.OtherMilkType)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  return (
    <Row>
      <Col xs={4}>
        <FormGroup>
          <p>
            <strong>Plant-Based Milk Alternative Type {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            options={milkTypeOptions}
            name="MilkType"
            control={methods.control}
            rules={{ required: true }}
            placeholder={'Select Plant-Based Milk Alternative Type'}
            onChange={([selected]) => {
              optionsReset(selected)
              setCurrent({
                ...current,
                MilkType: selected.value,
                category: selected.category,
                subCategory: selected.subCategory,
                IsBroadband: selected.isBroadband,
                OtherMilkType:
                  selected.value === 'Other' ? current.OtherMilkType : null,
              })
              return selected
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="MilkType"
              message={'⚠ Please select a Plant-Based Milk Alternative Type.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>

        {current?.MilkType === 'Other' && (
          <>
            <br />
            <FormGroup>
              <p>
                <strong>
                  Other Plant-Based Milk Alternative Type (Please Specify){' '}
                  {asterisk}
                </strong>
              </p>
              <Controller
                as={Input}
                name="OtherMilkType"
                placeholder={'Other Plant-Based Milk Alternative Type'}
                defaultValue={current?.OtherMilkType}
                control={methods.control}
                rules={{
                  required: {
                    value: true,
                    message:
                      '⚠ Other Plant-Based Milk Alternative Type is required.',
                  },
                  maxLength: {
                    value: 30,
                    message:
                      '⚠ Other Plant-Based Milk Alternative Type exceeds 30 characters.',
                  },
                }}
                onKeyDown={e => {
                  if (isNotAlpha(e.key) && e.key !== ' ') {
                    e.preventDefault()
                  }
                }}
                onChange={([event]) => {
                  onChange(event)
                  return event
                }}
              />
              <p className={'text-danger'}>
                <ErrorMessage
                  errors={methods.errors}
                  name="OtherMilkType"
                  as={<Alert color={'danger'} className={'mt-3'} />}
                />
              </p>
            </FormGroup>
          </>
        )}
        <br />
        <FormGroup>
          <p>
            <strong>Size of Individual Package {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            name="Size"
            defaultValue={packaging.Size}
            placeholder={'ounces'}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 6,
                message: '⚠ Size exceeds 6 characters.',
              },
              required: {
                value: true,
                message: '⚠ Please enter product size.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Size"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={4}>
        <FormGroup>
          <p>
            <strong>Flavor {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            options={flavorOptions}
            name="Flavor"
            control={methods.control}
            rules={{ required: true }}
            placeholder={'Select Flavor'}
            onChange={([selected]) => {
              setSelect(selected, 'Flavor')
              return selected
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Flavor"
              message={'⚠ Please select a flavor.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
        <br />
        <FormGroup>
          <p>
            <strong>Type {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            name="Type"
            placeholder={'Select Type'}
            options={typeOptions}
            onChange={([selected]) => {
              setSelect(selected, 'Type')
              return selected
            }}
            control={methods.control}
            rules={{ required: true }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Type"
              message={'⚠ Please select a type.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={4}>
        <FormGroup>
          <p>
            <strong>Organic</strong>
          </p>
          <Controller
            as={Switch}
            name="Organic"
            id="Organic"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.Organic}
          />
        </FormGroup>
        <br />
        <FormGroup>
          <p>
            <strong>Multi-pack</strong>
          </p>
          <Controller
            as={Switch}
            name="MultiPackageType"
            id="MultiPackageType"
            onChange={([checked, event, id]) => {
              setSwitch(checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.MultiPackageType}
          />
        </FormGroup>
        <br />
        {current?.MultiPackageType === true && (
          <FormGroup>
            <p>
              <strong>Multi-pack Count {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="MultiPackageCount"
              placeholder={'Multi-pack Count'}
              onKeyDown={e => {
                if (
                  ![
                    'Backspace',
                    'Delete',
                    'Tab',
                    'ArrowLeft',
                    'ArrowRight',
                  ].includes(e.key) &&
                  isNaN(parseInt(e.key))
                ) {
                  e.preventDefault()
                }
              }}
              control={methods.control}
              rules={{
                maxLength: { value: 2, message: '⚠ 2 characters max.' },
                required: {
                  value: true,
                  message: '⚠ Multi Pack Count is required.',
                },
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="MultiPackageCount"
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
      </Col>
      <br />
    </Row>
  )
}

export default PlantMilkPackage
